import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Undo, Redo
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      padding: '6px 8px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isActive ? '#dbeafe' : 'transparent',
      color: isActive ? '#2563eb' : '#374151',
      transition: 'all 0.1s ease'
    }}
    onMouseEnter={(e) => {
      if (!isActive) e.currentTarget.style.backgroundColor = '#f3f4f6';
    }}
    onMouseLeave={(e) => {
      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
    }}
  >
    {children}
  </button>
);

const Separator = () => (
  <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
);

function MenuBar({ editor }) {
  if (!editor) return null;

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '2px',
      padding: '8px 12px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#fafafa'
    }}>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold size={16} />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic size={16} />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon size={16} />
      </MenuButton>

      <Separator />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 size={16} />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 size={16} />
      </MenuButton>

      <Separator />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List size={16} />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered size={16} />
      </MenuButton>

      <Separator />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        isActive={false}
        title="Undo (Ctrl+Z)"
      >
        <Undo size={16} />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        isActive={false}
        title="Redo (Ctrl+Y)"
      >
        <Redo size={16} />
      </MenuButton>
    </div>
  );
}

function RichTextEditor({ content, onChange, editable = true, maxHeight }) {
  // Track the HTML we last emitted so we can distinguish a true external
  // content change from the parent echoing our own onUpdate back as a prop.
  // Without this, Tiptap's non-idempotent getHTML() (e.g. Enter, delete-empty)
  // makes the post-echo comparison fail and triggers setContent, which
  // resets the document and jumps the cursor to the end.
  const lastEmittedRef = useRef(content || '');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
    ],
    content: content || '',
    editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmittedRef.current = html;
      if (onChange) {
        onChange(html);
      }
    },
  });

  // Update content only on a true external change (not an echo of our own update)
  useEffect(() => {
    if (!editor || content === undefined) return;
    if (content === lastEmittedRef.current) return;
    if (content === editor.getHTML()) return;
    lastEmittedRef.current = content;
    editor.commands.setContent(content || '', false);
  }, [content, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  return (
    <div style={{
      border: editable ? '2px solid #9ca3af' : '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>
      {editable && <MenuBar editor={editor} />}
      <div
        style={{
          padding: '16px',
          minHeight: '400px',
          maxHeight: maxHeight || '600px',
          overflowY: 'auto',
          fontSize: '14px',
          lineHeight: '1.8',
          fontFamily: 'Arial, sans-serif',
          color: '#111827'
        }}
        className="rich-text-content"
      >
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .rich-text-content .ProseMirror {
          outline: none;
          min-height: 380px;
        }
        .rich-text-content .ProseMirror h2 {
          font-size: 18px;
          font-weight: 700;
          margin: 16px 0 8px;
          color: #111827;
        }
        .rich-text-content .ProseMirror h3 {
          font-size: 15px;
          font-weight: 700;
          margin: 12px 0 6px;
          color: #111827;
        }
        .rich-text-content .ProseMirror p {
          margin: 0 0 8px;
        }
        .rich-text-content .ProseMirror ul,
        .rich-text-content .ProseMirror ol {
          padding-left: 24px;
          margin: 4px 0 8px;
        }
        .rich-text-content .ProseMirror li {
          margin-bottom: 2px;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;
